import { useEffect, useMemo, useState } from "react";
import {
  ApiClientError,
  fetchAmazonCampaigns,
  fetchCampaignAds,
  fetchCampaigns,
  fetchGoogleCampaignAds,
  fetchGoogleCampaigns,
  setAdStatus,
  setAmazonCampaignBudget,
  setAmazonCampaignStatus,
  setGoogleCampaignBudget,
  setGoogleCampaignStatus,
} from "../api/client";
import { StateBanner } from "../components/StateBanner";
import { ChannelTabs } from "../components/ChannelTabs";
import { PeriodSwitcher } from "../components/PeriodSwitcher";
import { Badge } from "../components/Badge";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { CampaignAdsPanel, type AdsFetchState } from "../components/CampaignAdsPanel";
import { AmazonTargetsPanel } from "../components/AmazonTargetsPanel";
import { AmazonCampaignsTable } from "../components/AmazonCampaignsTable";
import { AmazonKwRankPanel } from "../components/AmazonKwRankPanel";
import { GoogleOpsCard } from "../components/GoogleOpsCard";
import {
  PLATFORM_META,
  PLATFORM_TABS,
  type PlatformFilter,
  amazonAdProductLabel,
  campaignStatusMeta,
  channelMeta,
  googleChannelTypeLabel,
  googlePrimaryStatusMeta,
  googleStrengthMeta,
  yen,
} from "../lib/meta";
import { usePeriod } from "../lib/usePeriod";
import type { AdPlatform, Campaign, ChannelFilter, GoogleAccountCheck } from "../types/contract";

/**
 * 媒体ごとの取得状態。Meta / Google / Amazon は別APIなので独立して読込/失敗する
 * （どれかが落ちても他は描画する・失敗は媒体名付きバナーで出す）。
 */
type PlatformFetchState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ok"; data: Campaign[]; account?: GoogleAccountCheck };

/** 日予算の月換算（Google の月間上限の考え方: 日予算×30.4）。 */
const MONTHLY_FACTOR = 30.4;

/** 媒体の表示順（行の並び・エラーバナーの並び）。媒体を足すときはここと fetchPlatform に追加する。 */
const AD_PLATFORMS: AdPlatform[] = ["meta", "google", "amazon"];

const PLATFORM_STORAGE_KEY = "stoq-ad-console:campaigns-platform:v1";

function readStoredPlatform(): PlatformFilter {
  try {
    const v = localStorage.getItem(PLATFORM_STORAGE_KEY);
    return v === "meta" || v === "google" || v === "amazon" || v === "all" ? v : "all";
  } catch {
    return "all";
  }
}

// 2026-10-30: Amazon タブの種別サブフィルタ（すべて/SP/SB/SD）。選択は端末ごとに保持する。
type AmazonProductFilter = "all" | "SP" | "SB" | "SD";
const AMAZON_PRODUCT_TABS: AmazonProductFilter[] = ["all", "SP", "SB", "SD"];
const AMAZON_PRODUCT_STORAGE_KEY = "stoq-ad-console:campaigns-amazon-product:v1";
const AMAZON_PRODUCT_BY_FILTER: Record<Exclude<AmazonProductFilter, "all">, string> = {
  SP: "SPONSORED_PRODUCTS",
  SB: "SPONSORED_BRANDS",
  SD: "SPONSORED_DISPLAY",
};

function readStoredAmazonProduct(): AmazonProductFilter {
  try {
    const v = localStorage.getItem(AMAZON_PRODUCT_STORAGE_KEY);
    return v === "SP" || v === "SB" || v === "SD" || v === "all" ? v : "all";
  } catch {
    return "all";
  }
}

function platformOf(c: Campaign): AdPlatform {
  return c.platform === "google" || c.platform === "amazon" ? c.platform : "meta";
}

export function CampaignsPage() {
  const { period, customStart, customEnd, range, setPeriod, setCustomStart, setCustomEnd } = usePeriod();
  const [platform, setPlatform] = useState<PlatformFilter>(readStoredPlatform);
  const [amazonProduct, setAmazonProduct] = useState<AmazonProductFilter>(readStoredAmazonProduct);
  const [channel, setChannel] = useState<ChannelFilter>("all");
  const [search, setSearch] = useState("");
  const [states, setStates] = useState<Record<AdPlatform, PlatformFetchState>>({
    meta: { status: "idle" },
    google: { status: "idle" },
    amazon: { status: "idle" },
  });
  const setPlatformState = (p: AdPlatform, s: PlatformFetchState) => setStates((prev) => ({ ...prev, [p]: s }));
  const [pendingAction, setPendingAction] = useState<{
    campaign: Campaign;
    action: "pause" | "activate";
  } | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Google / Amazon 日予算の変更（塩田さん推奨「予算アロケーション」をコンソールで管理）: 行内で編集 → 確認 → POST。
  const [budgetEdit, setBudgetEdit] = useState<{ id: string; value: string } | null>(null);
  const [pendingBudget, setPendingBudget] = useState<{ campaign: Campaign; value: number } | null>(null);
  const [budgetBusy, setBudgetBusy] = useState(false);
  const [budgetError, setBudgetError] = useState<string | null>(null);

  // 「配信中の広告」展開パネル: 単一展開のみ（別行クリックで切替・再クリックで閉じる）。
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [adsState, setAdsState] = useState<Record<string, AdsFetchState>>({});

  const changePlatform = (p: PlatformFilter) => {
    setPlatform(p);
    setExpandedId(null);
    try {
      localStorage.setItem(PLATFORM_STORAGE_KEY, p);
    } catch {
      /* 保存できなくても動作には影響しない */
    }
  };

  const changeAmazonProduct = (p: AmazonProductFilter) => {
    setAmazonProduct(p);
    setExpandedId(null);
    try {
      localStorage.setItem(AMAZON_PRODUCT_STORAGE_KEY, p);
    } catch {
      /* 保存できなくても動作には影響しない */
    }
  };

  const loadAds = (campaign: Campaign) => {
    const key = campaign.id;
    setAdsState((prev) => ({ ...prev, [key]: { status: "loading" } }));
    const req =
      platformOf(campaign) === "google"
        ? fetchGoogleCampaignAds(campaign.id, { range, channel: campaign.channel })
        : fetchCampaignAds(campaign.id, { range, channel: campaign.channel });
    req
      .then((res) => setAdsState((prev) => ({ ...prev, [key]: { status: "ok", data: res.ads } })))
      .catch((e: unknown) => {
        const message = e instanceof ApiClientError ? e.message : "予期しないエラーが発生しました。";
        setAdsState((prev) => ({ ...prev, [key]: { status: "error", message } }));
      });
  };

  const toggleAds = (campaign: Campaign) => {
    if (expandedId === campaign.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(campaign.id);
    // Amazon は AmazonTargetsPanel が自分で広告グループ/ターゲットを取得する（広告単位の API は無い）
    if (platformOf(campaign) !== "amazon") loadAds(campaign); // 期間が変わっている可能性があるため展開の都度取得し直す
  };

  /** 媒体ごとの一覧取得。Amazon は BFF がチャネル/検索語を受けないので、ここで同じ条件を当てて揃える。 */
  const fetchPlatform = async (p: AdPlatform): Promise<{ data: Campaign[]; account?: GoogleAccountCheck }> => {
    if (p === "meta") return { data: (await fetchCampaigns(channel, search, range)).campaigns };
    if (p === "google") {
      const res = await fetchGoogleCampaigns(channel, search, range);
      return { data: res.campaigns, account: res.account };
    }
    const res = await fetchAmazonCampaigns(range.from, range.to);
    return {
      data: res.campaigns.filter((c) => (channel === "all" || c.channel === channel) && (!search || c.name.includes(search))),
    };
  };

  const load = () => {
    for (const p of AD_PLATFORMS) {
      if (platform === "all" || platform === p) {
        setPlatformState(p, { status: "loading" });
        fetchPlatform(p)
          .then((res) => setPlatformState(p, { status: "ok", data: res.data, account: res.account }))
          .catch((e: unknown) => {
            const message = e instanceof ApiClientError ? e.message : "予期しないエラーが発生しました。";
            setPlatformState(p, { status: "error", message });
          });
      } else {
        setPlatformState(p, { status: "idle" });
      }
    }
  };

  useEffect(() => {
    const t = setTimeout(load, search ? 250 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform, channel, search, range.from, range.to]);

  // 媒体横断の表示行: Meta → Google → Amazon の順（同一媒体内はAPIの並び順を維持）。
  const rows = useMemo(() => {
    const out: Campaign[] = [];
    for (const p of AD_PLATFORMS) {
      const s = states[p];
      if (s.status === "ok") out.push(...s.data);
    }
    return out;
  }, [states]);

  // 2026-10-30: Amazon タブ専用の表示行（チャネル/検索適用済みの Amazon 行 → 種別サブフィルタ）。件数は絞り込み前の live 件数。
  const amazonRows = useMemo(() => rows.filter((c) => platformOf(c) === "amazon"), [rows]);
  const amazonCounts = useMemo(() => {
    const n = { all: amazonRows.length, SP: 0, SB: 0, SD: 0 };
    for (const c of amazonRows) {
      for (const k of ["SP", "SB", "SD"] as const) {
        if (c.amazon?.ad_product === AMAZON_PRODUCT_BY_FILTER[k]) n[k] += 1;
      }
    }
    return n;
  }, [amazonRows]);
  const amazonVisibleRows = useMemo(
    () =>
      amazonProduct === "all"
        ? amazonRows
        : amazonRows.filter((c) => c.amazon?.ad_product === AMAZON_PRODUCT_BY_FILTER[amazonProduct]),
    [amazonRows, amazonProduct]
  );

  const anyLoading = AD_PLATFORMS.some((p) => states[p].status === "loading");
  const allSettled = !anyLoading;
  const errors = AD_PLATFORMS.flatMap((p) => {
    const s = states[p];
    return s.status === "error" ? [{ platform: p, message: s.message }] : [];
  });
  // 取得対象の媒体がすべて失敗したときだけ全面エラー（「すべての媒体」= 全媒体、単一タブ = その1媒体）
  const wantedCount = platform === "all" ? AD_PLATFORMS.length : 1;
  const everythingFailed = allSettled && errors.length > 0 && rows.length === 0 && errors.length === wantedCount;

  const openConfirm = (campaign: Campaign, action: "pause" | "activate") => {
    setActionError(null);
    setPendingAction({ campaign, action });
  };

  const runAction = async () => {
    if (!pendingAction) return;
    const { campaign, action } = pendingAction;
    const pf = platformOf(campaign);
    if (pf === "meta" && !campaign.ad_id) return;
    setActionBusy(true);
    setActionError(null);
    try {
      if (pf === "google") await setGoogleCampaignStatus(campaign.id, action);
      else if (pf === "amazon") await setAmazonCampaignStatus(campaign.id, action, campaign.amazon?.ad_product);
      else await setAdStatus(campaign.ad_id as string, action);
      setPendingAction(null);
      load();
    } catch (e) {
      setActionError(e instanceof ApiClientError ? e.message : "操作に失敗しました。");
    } finally {
      setActionBusy(false);
    }
  };

  const runBudget = async () => {
    if (!pendingBudget) return;
    setBudgetBusy(true);
    setBudgetError(null);
    try {
      if (platformOf(pendingBudget.campaign) === "amazon") {
        await setAmazonCampaignBudget(pendingBudget.campaign.id, pendingBudget.value, pendingBudget.campaign.amazon?.ad_product);
      } else {
        await setGoogleCampaignBudget(pendingBudget.campaign.id, pendingBudget.value);
      }
      setPendingBudget(null);
      setBudgetEdit(null);
      load();
    } catch (e) {
      setBudgetError(e instanceof ApiClientError ? e.message : "予算の変更に失敗しました。");
    } finally {
      setBudgetBusy(false);
    }
  };

  const submitBudgetEdit = (campaign: Campaign) => {
    if (!budgetEdit) return;
    const value = Number(budgetEdit.value.replace(/[,，¥\s]/g, ""));
    if (!Number.isInteger(value) || value < 100) {
      setBudgetError("日予算は100円以上の整数で入力してください。");
      return;
    }
    setBudgetError(null);
    setPendingBudget({ campaign, value });
  };

  const expandedCampaign = expandedId ? rows.find((c) => c.id === expandedId) ?? null : null;
  const googleAccount = states.google.status === "ok" ? states.google.account : undefined;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">キャンペーン</h1>
        <PeriodSwitcher
          period={period}
          onChange={setPeriod}
          customStart={customStart}
          customEnd={customEnd}
          onCustomStartChange={setCustomStart}
          onCustomEndChange={setCustomEnd}
        />
        <input
          className="search-input"
          placeholder="キャンペーン名で検索"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <div className="platform-row">
        <div className="tab-row" role="tablist" aria-label="媒体">
          {PLATFORM_TABS.map((id) => (
            <button
              key={id}
              role="tab"
              aria-selected={platform === id}
              className={`tab-btn ${platform === id ? "active" : ""}`}
              onClick={() => changePlatform(id)}
            >
              {PLATFORM_META[id].label}
            </button>
          ))}
        </div>
        <span className="platform-note">
          Google広告は 2026-10-14 配信開始。キャンペーン単位で一時停止/再開と日予算の変更ができます（入札はGoogle広告の管理画面で）。
          Amazon広告（11/2 配信開始）は許可リストに入ったキャンペーンだけ一時停止/再開・日予算・ターゲット入札・除外キーワードを操作できます。売上はAmazon計測値（7日/14日アトリビューション）で、他媒体とは基準が異なります。
        </span>
      </div>
      {platform === "amazon" && (
        <div className="platform-row">
          <div className="tab-row" role="tablist" aria-label="広告種別">
            {AMAZON_PRODUCT_TABS.map((id) => (
              <button
                key={id}
                role="tab"
                aria-selected={amazonProduct === id}
                className={`tab-btn ${amazonProduct === id ? "active" : ""}`}
                onClick={() => changeAmazonProduct(id)}
              >
                {id === "all" ? "すべて" : id}({amazonCounts[id]})
              </button>
            ))}
          </div>
        </div>
      )}
      <div style={{ marginBottom: 12 }}>
        <ChannelTabs value={channel} onChange={setChannel} />
      </div>

      {errors.length > 0 && !everythingFailed && (
        <div className="partial-errors">
          {errors.map((e) => (
            <StateBanner
              key={e.platform}
              kind="error"
              message={`${PLATFORM_META[e.platform].label}広告の取得に失敗しました: ${e.message}`}
              onRetry={load}
            />
          ))}
        </div>
      )}
      {everythingFailed && (
        <StateBanner
          kind="error"
          message={errors.map((e) => `${PLATFORM_META[e.platform].label}: ${e.message}`).join(" / ")}
          onRetry={load}
        />
      )}

      {platform !== "meta" && <GoogleOpsCard campaigns={rows} account={googleAccount} monthlyFactor={MONTHLY_FACTOR} />}

      {anyLoading && rows.length === 0 && (
        <div className="table-wrap">
          <div style={{ padding: 16 }} className="skeleton">
            &nbsp;
          </div>
        </div>
      )}
      {allSettled && !everythingFailed && rows.length === 0 && (
        <div className="empty-state">該当するキャンペーンはありません。</div>
      )}
      {platform === "amazon" && <AmazonKwRankPanel range={range} />}
      {platform === "amazon" && amazonRows.length > 0 && (
        <AmazonCampaignsTable
          rows={amazonVisibleRows}
          grouped={amazonProduct === "all"}
          budgetEdit={budgetEdit}
          setBudgetEdit={setBudgetEdit}
          budgetError={budgetError}
          setBudgetError={setBudgetError}
          submitBudgetEdit={submitBudgetEdit}
          openConfirm={openConfirm}
          toggleAds={toggleAds}
          expandedId={expandedId}
        />
      )}
      {platform === "amazon" && amazonRows.length > 0 && amazonVisibleRows.length === 0 && (
        <div className="empty-state">この広告種別のキャンペーンはありません。</div>
      )}
      {platform === "amazon" && amazonRows.length > 0 && amazonVisibleRows.length === 0 && (
        <div className="empty-state">この広告種別のキャンペーンはありません。</div>
      )}
      {platform !== "amazon" && rows.length > 0 && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>キャンペーン名</th>
                <th>媒体</th>
                <th>チャネル</th>
                <th>ステータス</th>
                <th>日予算</th>
                <th>広告費</th>
                <th>clicks</th>
                <th>CTR</th>
                <th>CPC</th>
                <th>成果</th>
                <th>主KPI単価</th>
                <th>購入額</th>
                {/* 2026-09-15 B′: 楽天CAPI RakutenPurchase（カスタムCV）。標準purchaseとは別集計・全チャネル共通（Googleは常に—） */}
                <th>楽天購入</th>
                <th>楽天購入額</th>
                <th>ROAS</th>
                <th>開始日</th>
                <th>操作</th>
                <th>広告</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const pf = platformOf(c);
                const pfMeta = PLATFORM_META[pf];
                const chMeta = channelMeta(c.channel);
                const stMeta = campaignStatusMeta(c.status);
                const gPrimary = pf === "google" ? googlePrimaryStatusMeta(c.google?.primary_status) : null;
                const amz = pf === "amazon" ? c.amazon : undefined;
                const amzProduct = amz ? amazonAdProductLabel(amz.ad_product) : null;
                const isActive = c.status === "ACTIVE";
                const canOperate = c.operable && (pf !== "meta" || !!c.ad_id);
                const canEditBudget = pf !== "meta" && c.operable;
                return (
                  <tr key={`${pf}:${c.id}`}>
                    <td style={{ whiteSpace: "normal", minWidth: 220 }}>
                      {c.name}
                      {pf === "google" && (
                        <span className="cell-sub">
                          {googleChannelTypeLabel(c.google?.channel_type)}
                          {c.google?.end ? ` · 終了 ${c.google.end}` : ""}
                        </span>
                      )}
                      {amz && amzProduct && (
                        <span className="badge-stack amz-badges">
                          <span title={amzProduct.long}>
                            <Badge label={amzProduct.short} color="var(--pf-amazon-c)" bg="var(--pf-amazon-bg)" />
                          </span>
                          {amz.targeting_type && (
                            <Badge label={amz.targeting_type} color="var(--ink-sub)" bg="var(--ch-all-bg)" />
                          )}
                          {amz.acos != null && <span className="cell-sub amz-acos">ACOS {amz.acos.toFixed(1)}%</span>}
                        </span>
                      )}
                    </td>
                    <td>
                      <Badge label={pfMeta.label} color={pfMeta.c} bg={pfMeta.bg} />
                    </td>
                    <td>
                      <Badge label={chMeta.label} color={chMeta.c} bg={chMeta.bg} />
                    </td>
                    <td>
                      <span className="badge-stack">
                        <Badge label={stMeta.label} color={stMeta.c} bg={stMeta.bg} />
                        {gPrimary && <Badge label={gPrimary.label} color={gPrimary.c} bg={gPrimary.bg} />}
                        {pf === "google" && c.google?.ops?.learning.active && (
                          <Badge
                            label={`学習期間中 残${c.google.ops.learning.days_left ?? "?"}日`}
                            color="var(--st-pending-c)"
                            bg="var(--st-pending-bg)"
                          />
                        )}
                        {pf === "google" && c.google?.ops && (() => {
                          const s = googleStrengthMeta(c.google.ops.ad_strength.min);
                          return <Badge label={s.label} color={s.c} bg={s.bg} />;
                        })()}
                      </span>
                    </td>
                    <td>
                      {canEditBudget && budgetEdit?.id === c.id ? (
                        <span className="budget-edit">
                          <input
                            type="text"
                            inputMode="numeric"
                            value={budgetEdit.value}
                            aria-label="日予算（円）"
                            onChange={(e) => setBudgetEdit({ id: c.id, value: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") submitBudgetEdit(c);
                              if (e.key === "Escape") setBudgetEdit(null);
                            }}
                            autoFocus
                          />
                          <button className="btn btn-sm" onClick={() => submitBudgetEdit(c)}>
                            保存
                          </button>
                          <button className="link-btn" onClick={() => { setBudgetEdit(null); setBudgetError(null); }}>
                            取消
                          </button>
                          {budgetError && <span className="ad-card-copy-failed">{budgetError}</span>}
                        </span>
                      ) : (
                        <span className="budget-cell">
                          {c.daily_budget != null ? yen(c.daily_budget) : "—"}
                          {amz?.budget_usage_pct != null && (
                            <span className="cell-sub">当日消化 {Math.round(amz.budget_usage_pct)}%</span>
                          )}
                          {canEditBudget && (
                            <button
                              className="link-btn"
                              onClick={() => { setBudgetError(null); setBudgetEdit({ id: c.id, value: String(c.daily_budget ?? "") }); }}
                              title={pf === "amazon" ? "日予算を変更（Amazon広告のキャンペーン予算を書き換えます）" : "日予算を変更（Google広告のキャンペーン予算を書き換えます）"}
                            >
                              変更
                            </button>
                          )}
                        </span>
                      )}
                    </td>
                    <td>{yen(c.spend)}</td>
                    <td>{c.clicks.toLocaleString("ja-JP")}</td>
                    <td>{c.ctr}</td>
                    <td>{c.cpc != null ? yen(c.cpc) : "—"}</td>
                    <td>
                      {c.primary.count.toLocaleString("ja-JP")}件（{c.primary.label}）
                    </td>
                    <td>{c.primary.value != null ? yen(c.primary.value) : "—"}</td>
                    <td>{c.purchase_value > 0 ? yen(c.purchase_value) : "—"}</td>
                    <td>{c.rakuten_purchases > 0 ? `${c.rakuten_purchases.toLocaleString("ja-JP")}件` : "—"}</td>
                    <td>{c.rakuten_purchase_value > 0 ? yen(c.rakuten_purchase_value) : "—"}</td>
                    <td>{c.roas != null ? `${c.roas.toFixed(2)}x` : "—"}</td>
                    <td>{c.start ?? "—"}</td>
                    <td>
                      {canOperate ? (
                        <button
                          className="btn btn-sm"
                          onClick={() => openConfirm(c, isActive ? "pause" : "activate")}
                          title={pf !== "meta" ? "キャンペーン本体の配信ステータスを変更します" : undefined}
                        >
                          {isActive ? "一時停止" : "再開"}
                        </button>
                      ) : (
                        <span className="text-faint" style={{ color: "var(--ink-faint)", fontSize: 11.5 }}>—</span>
                      )}
                    </td>
                    <td>
                      <button className="btn btn-sm" onClick={() => toggleAds(c)}>
                        {pf === "amazon"
                          ? expandedId === c.id ? "ターゲットを閉じる" : "ターゲットを見る"
                          : expandedId === c.id ? "広告を閉じる" : "広告を見る"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {expandedId && (
        <div className="ads-panel-wrap">
          <div className="ads-panel-header">
            <span className="ads-panel-campaign-name">
              {expandedCampaign && (
                <Badge
                  label={PLATFORM_META[platformOf(expandedCampaign)].label}
                  color={PLATFORM_META[platformOf(expandedCampaign)].c}
                  bg={PLATFORM_META[platformOf(expandedCampaign)].bg}
                />
              )}{" "}
              {expandedCampaign?.name ?? expandedId}
            </span>
            <button className="btn btn-sm" onClick={() => setExpandedId(null)}>
              閉じる
            </button>
          </div>
          {expandedCampaign && platformOf(expandedCampaign) === "amazon" ? (
            <AmazonTargetsPanel campaign={expandedCampaign} range={range} />
          ) : (
            <CampaignAdsPanel
              state={adsState[expandedId] ?? { status: "loading" }}
              onRetry={() => expandedCampaign && loadAds(expandedCampaign)}
              onChanged={() => expandedCampaign && loadAds(expandedCampaign)}
            />
          )}
        </div>
      )}

      {pendingBudget && (
        <ConfirmDialog
          title="日予算を変更しますか？"
          description={`「${pendingBudget.campaign.name}」の日予算を ${
            pendingBudget.campaign.daily_budget != null ? yen(pendingBudget.campaign.daily_budget) : "—"
          } → ${yen(pendingBudget.value)}（月換算 約${yen(Math.round(pendingBudget.value * MONTHLY_FACTOR))}）に変更します。${
            pendingBudget.campaign.google?.ops?.learning.active
              ? ` ⚠ 学習期間中です（残り${pendingBudget.campaign.google.ops.learning.days_left ?? "?"}日）。大きな変更は学習をリセットするおそれがあります。`
              : ""
          }`}
          confirmLabel="変更する"
          danger={!!pendingBudget.campaign.google?.ops?.learning.active}
          onConfirm={runBudget}
          onCancel={() => setPendingBudget(null)}
          busy={budgetBusy}
          errorMessage={budgetError}
        />
      )}

      {pendingAction && (
        <ConfirmDialog
          title={pendingAction.action === "pause" ? "配信を一時停止しますか？" : "配信を再開しますか？"}
          description={`「${pendingAction.campaign.name}」${
            platformOf(pendingAction.campaign) === "google" ? "（Google広告・キャンペーン本体）" : platformOf(pendingAction.campaign) === "amazon" ? "（Amazon広告・キャンペーン本体）" : ""
          }の配信ステータスを${pendingAction.action === "pause" ? "一時停止" : "配信中"}に変更します。${
            platformOf(pendingAction.campaign) === "google" && pendingAction.action === "activate" && pendingAction.campaign.daily_budget != null
              ? ` 日予算 ${yen(pendingAction.campaign.daily_budget)}（月換算 約${yen(Math.round(pendingAction.campaign.daily_budget * MONTHLY_FACTOR))}）で配信されます。開始後約2週間は学習期間として設定変更を静観してください。`
              : ""
          }`}
          confirmLabel={pendingAction.action === "pause" ? "一時停止する" : "再開する"}
          danger={pendingAction.action === "pause"}
          onConfirm={runAction}
          onCancel={() => setPendingAction(null)}
          busy={actionBusy}
          errorMessage={actionError}
        />
      )}
    </div>
  );
}
